import React, { useState } from 'react';
import { Modal, Box, LinearProgress, IconButton } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { DateTime } from 'luxon';
import { LoyaltyLevelGradesModal } from './LoyaltyLevelGradesModal';
import { LoyaltyBonusesModal } from './LoyaltyBonusesModal';

export const LoyaltyStatsModal = ({ open, onClose, loyaltyData }) => {
    const [gradesOpen, setGradesOpen] = useState(false);
    const [bonusesOpen, setBonusesOpen] = useState(false);

    if (!loyaltyData) return null;

    const { Level, CreatedAtUtc, Participations, Wins, Payments } = loyaltyData;

    const joinDate = CreatedAtUtc
        ? DateTime.fromISO(CreatedAtUtc).toFormat('dd.MM.yyyy')
        : '';

    const participationsProgress = calcProgress(Participations);
    const winsProgress = calcProgress(Wins);
    const paymentsProgress = calcProgress(Payments);

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box className="modal_body loyalty_stats_modal">
                    <div className="loyalty_stats_header">
                        <div className="loyalty_stats_header_left">
                            <div className="loyalty_stats_icon_wrap">
                                <img
                                    src={`/icons/loyalty/${Level}.svg`}
                                    alt={`${Level} уровень`}
                                    style={{ width: 64, height: 64 }}
                                />
                            </div>
                            <div className="loyalty_stats_title_block">
                                <div className="loyalty_stats_title">Моя программа лояльности</div>
                                <div className="loyalty_stats_level">Текущий уровень: {Level}</div>
                                {joinDate && (
                                    <div className="loyalty_stats_date">В программе с {joinDate}</div>
                                )}
                            </div>
                        </div>
                        <div className="loyalty_stats_links">
                            <div className="loyalty_stats_link" onClick={() => setGradesOpen(true)}>
                                <HelpOutlineIcon fontSize="small" className="loyalty_link_icon" />
                                <span>Уровни</span>
                                <ChevronRightIcon fontSize="small" />
                            </div>
                            <div className="loyalty_stats_link" onClick={() => setBonusesOpen(true)}>
                                <HelpOutlineIcon fontSize="small" className="loyalty_link_icon" />
                                <span>Бонусы</span>
                                <ChevronRightIcon fontSize="small" />
                            </div>
                        </div>
                        <IconButton size="small" onClick={onClose} className="loyalty_modal_close_btn">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>

                    <div className="loyalty_upgrade_banner">
                        <div className="loyalty_upgrade_banner_icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 4L4 12H9V20H15V12H20L12 4Z" fill="currentColor"/>
                            </svg>
                        </div>
                        <div>
                            <div className="loyalty_upgrade_banner_title">Повышайте уровень любым способом</div>
                            <div className="loyalty_upgrade_banner_subtitle">Каждый показатель может повысить ваш уровень.</div>
                        </div>
                    </div>

                    <div className="loyalty_progress_section_title">Условия повышения уровня</div>

                    <div className="loyalty_progress_row">
                        <PeopleAltOutlinedIcon className="loyalty_progress_icon loyalty_progress_icon_blue" />
                        <div className="loyalty_progress_info">
                            <div className="loyalty_progress_label">Участия</div>
                            <div className="loyalty_progress_sublabel">Текущее: {Participations?.Value ?? 0}</div>
                        </div>
                        <div className="loyalty_progress_bar_wrap">
                            <LinearProgress
                                variant="determinate"
                                value={participationsProgress}
                                className="loyalty_progress_bar loyalty_progress_bar_blue"
                            />
                        </div>
                        <div className="loyalty_progress_value">
                            <div className="loyalty_progress_value_main">{Participations?.ToNextLevel ?? 0}</div>
                            <div className="loyalty_progress_value_sub">до повышения уровня</div>
                        </div>
                    </div>

                    <div className="loyalty_progress_row">
                        <EmojiEventsOutlinedIcon className="loyalty_progress_icon loyalty_progress_icon_yellow" />
                        <div className="loyalty_progress_info">
                            <div className="loyalty_progress_label">Победы</div>
                            <div className="loyalty_progress_sublabel">Текущее: {Wins?.Value ?? 0}</div>
                        </div>
                        <div className="loyalty_progress_bar_wrap">
                            <LinearProgress
                                variant="determinate"
                                value={winsProgress}
                                className="loyalty_progress_bar loyalty_progress_bar_yellow"
                            />
                        </div>
                        <div className="loyalty_progress_value">
                            <div className="loyalty_progress_value_main">{Wins?.ToNextLevel ?? 0}</div>
                            <div className="loyalty_progress_value_sub">до повышения уровня</div>
                        </div>
                    </div>

                    <div className="loyalty_progress_row">
                        <CardGiftcardOutlinedIcon className="loyalty_progress_icon loyalty_progress_icon_purple" />
                        <div className="loyalty_progress_info">
                            <div className="loyalty_progress_label">Вознаграждения</div>
                            <div className="loyalty_progress_sublabel">Текущее: {formatMoney(Payments?.Value ?? 0)} ₽</div>
                        </div>
                        <div className="loyalty_progress_bar_wrap">
                            <LinearProgress
                                variant="determinate"
                                value={paymentsProgress}
                                className="loyalty_progress_bar loyalty_progress_bar_purple"
                            />
                        </div>
                        <div className="loyalty_progress_value">
                            <div className="loyalty_progress_value_main">{formatMoney(Payments?.ToNextLevel ?? 0)} ₽</div>
                            <div className="loyalty_progress_value_sub">до повышения уровня</div>
                        </div>
                    </div>
                </Box>
            </Modal>

            <LoyaltyLevelGradesModal open={gradesOpen} onClose={() => setGradesOpen(false)} />
            <LoyaltyBonusesModal open={bonusesOpen} onClose={() => setBonusesOpen(false)} />
        </>
    );
};

function calcProgress(range) {
    if (!range) return 0;
    const min = range.Min ?? 0;
    const max = range.Max ?? 0;
    const value = range.Value ?? 0;
    if (max <= min) return 0;
    const pct = ((value - min) / (max - min)) * 100;
    return Math.min(100, Math.max(0, pct));
}

function formatMoney(value) {
    if (value === undefined || value === null) return '0';
    return value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}
